/**
 * Onboarding step 1 — Company profile.
 *
 * Name / website / one-paragraph description, plus a "Scrape website"
 * button riding the shared enrichment seam (src/lib/agent/enrich.ts):
 * a real worker when VITE_ENRICH_API_URL is configured, otherwise the
 * deterministic local simulation — labelled honestly with a Simulated
 * pill so nothing pretends to be a live scrape.
 */

import { useState } from 'react';
import { Globe, Sparkles } from 'lucide-react';
import { enrichProspect, isConfigured } from '../../../lib/agent/enrich';
import type { OnboardingDraft } from '../../../lib/onboarding';

interface Props {
  draft: OnboardingDraft;
  onPatch: (patch: Partial<OnboardingDraft>) => void;
}

export function StepCompany({ draft, onPatch }: Props) {
  const [scraping, setScraping] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const configured = isConfigured();

  const setCompany = (patch: Partial<OnboardingDraft['company']>) =>
    onPatch({ company: { ...draft.company, ...patch } });

  const scrape = async () => {
    const website = draft.company.website.trim();
    if (!website || scraping) return;
    setScraping(true);
    setNote(null);
    try {
      const read = await enrichProspect({
        name: draft.company.name.trim() || website,
        company: draft.company.name.trim() || undefined,
        website,
      });
      const summary = read.summary?.trim() ?? '';
      if (summary && !draft.company.description.trim()) {
        setCompany({ description: summary });
      }
      setNote(
        configured
          ? 'Extracted from your website via the enrichment worker — edit freely.'
          : 'Simulated extraction (no live scrape — deterministic local read). Edit it into your own words.',
      );
    } finally {
      setScraping(false);
    }
  };

  return (
    <div className="onb-step">
      <h3 className="t-section-title onb-step__title">Company profile</h3>
      <p className="t-meta onb-step__blurb">
        This seeds your Company Brain — every agent and draft downstream reads it.
      </p>

      <label className="t-eyebrow onb-label" htmlFor="onb-company-name">
        Company name
      </label>
      <input
        id="onb-company-name"
        className="glass-input"
        value={draft.company.name}
        onChange={(e) => setCompany({ name: e.target.value })}
        placeholder="Acme Robotics"
        autoFocus
      />

      <label className="t-eyebrow onb-label" htmlFor="onb-company-website">
        Website
      </label>
      <div className="onb-row">
        <input
          id="onb-company-website"
          className="glass-input"
          value={draft.company.website}
          onChange={(e) => setCompany({ website: e.target.value })}
          placeholder="https://acme.dev"
          style={{ flex: 1 }}
        />
        <button
          type="button"
          className="glass-btn"
          onClick={() => void scrape()}
          disabled={!draft.company.website.trim() || scraping}
          title={
            configured
              ? 'Extract a description via the enrichment worker'
              : 'No enrichment worker configured — runs an honest local simulation'
          }
        >
          <Globe size={13} /> {scraping ? 'Scraping…' : 'Scrape website'}
        </button>
        <span
          className="onb-pill-tag"
          data-live={configured ? 'true' : 'false'}
          title={
            configured
              ? 'Live enrichment worker configured'
              : 'Deterministic local simulation — no network, honestly labelled'
          }
        >
          <Sparkles size={11} /> {configured ? 'Live' : 'Simulated'}
        </span>
      </div>

      <label className="t-eyebrow onb-label" htmlFor="onb-company-description">
        What does the company do? (one paragraph)
      </label>
      <textarea
        id="onb-company-description"
        className="glass-input onb-textarea"
        rows={4}
        value={draft.company.description}
        onChange={(e) => setCompany({ description: e.target.value })}
        placeholder="We build…"
      />
      {note && <p className="t-meta onb-note">{note}</p>}
    </div>
  );
}
